// src/components/MyLocationMap.jsx

import React, { useMemo, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { GoogleMap } from "@react-google-maps/api";
import useGoogleMaps from "~/provider/useGoogleMaps";

const containerStyle = {
  width: "100%",
  height: "500px",
};

function MyLocationMap({ coordinates, location }) {
  const { isLoaded } = useGoogleMaps();

  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const infoRef = useRef(null);

  const center = useMemo(
    () => ({
      lat: Number(coordinates.lat),
      lng: Number(coordinates.lng),
    }),
    [coordinates.lat, coordinates.lng]
  );

  // Create + update marker and InfoWindow
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;

    // Create marker once
    if (!markerRef.current) {
      markerRef.current = new window.google.maps.Marker({
        map: mapRef.current,
        position: center,
      });
    } else {
      markerRef.current.setPosition(center);
    }

    // Create InfoWindow once
    if (!infoRef.current) {
      infoRef.current = new window.google.maps.InfoWindow();
    }

    infoRef.current.setContent(`
      <h3>${location}</h3>
      <p><b>Latitude:</b> ${center.lat}</p>
      <p><b>Longitude:</b> ${center.lng}</p>
    `);

    infoRef.current.open({
      map: mapRef.current,
      anchor: markerRef.current,
    });
  }, [center, location, isLoaded]);

  const onLoad = (map) => {
    mapRef.current = map;
    map.setCenter(center);
    map.setZoom(16);
  };

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={16}
      mapTypeId="hybrid"
      onLoad={onLoad}
    />
  ) : null;
}

MyLocationMap.propTypes = {
  coordinates: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }).isRequired,
  location: PropTypes.string.isRequired,
};

export default React.memo(MyLocationMap);
